import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConnectionError from "../../../components/ConnectionError/ConnectionError";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Modal } from "../../../components/common/Modal/Modal";
import { ConfirmationModal } from "../../../components/common/ConfirmationModal/ConfirmationModal";
import { Pagination } from "../../../components/common/Pagination/Pagination";
import { useShowScrollTop } from "../../../hooks/useShowScrollTop";
import { adminService } from "../../../services/adminService";
import { adminProductService } from "../../../services/adminProductService";
import { publicContentService } from "../../../services/publicContentService";
import { resolveApiUrl } from "../../../services/api";
import { getErrorMessage } from "../../../utils/errors";
import type { ProductItem, ProductRequestData, ServiceItem } from "../../../types";
import "./ProductsPage.css";

interface ProductFormState {
  nameEn: string;
  nameAr: string;
  descEn: string;
  descAr: string;
  sizeEn: string;
  sizeAr: string;
  serviceId: string;
  sortOrder: string;
}

const emptyForm: ProductFormState = {
  nameEn: "",
  nameAr: "",
  descEn: "",
  descAr: "",
  sizeEn: "",
  sizeAr: "",
  serviceId: "",
  sortOrder: "0",
};

interface NewImage {
  file: File;
  preview: string;
}

function ProductsPage() {
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [serviceOptions, setServiceOptions] = useState<ServiceItem[]>([]);

  const isArabic = i18n.resolvedLanguage?.startsWith("ar") ?? false;

  // Pagination & Loading State
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");

  // Form Modal State (shared for create/edit)
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductItem | null>(null);
  const [form, setForm] = useState<ProductFormState>(emptyForm);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<NewImage[]>([]);
  const [saving, setSaving] = useState(false);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ProductItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Floating Go to Top hook
  const { showScrollTop, scrollToTop } = useShowScrollTop(300);

  // Load services for the filter & form dropdowns (small unpaginated list)
  useEffect(() => {
    publicContentService
      .getServices()
      .then(setServiceOptions)
      .catch(() => {});
  }, []);

  // Smart debounce: 700ms — enough time to finish typing.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 700);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load a specific page (replaces list content)
  const loadProducts = useCallback(
    async (pageToLoad: number) => {
      setIsFetching(true);
      setError(false);
      try {
        const response = await adminProductService.getAdminProducts({
          search: debouncedSearch,
          serviceId: serviceFilter,
          page: pageToLoad,
          size: 10,
        });
        setProducts(response.content);
        setPage(pageToLoad);
        setTotalPages(response.totalPages);
      } catch {
        setError(true);
      } finally {
        setIsFetching(false);
        setInitialLoading(false);
      }
    },
    [debouncedSearch, serviceFilter]
  );

  // Re-fetch from page 0 whenever the debounced search or service filter changes
  useEffect(() => {
    loadProducts(0);
  }, [loadProducts]);

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
  };

  // Clean up object URL previews
  useEffect(() => {
    return () => {
      newImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, [newImages]);

  const clearFormImages = () => {
    newImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setNewImages([]);
    setExistingImages([]);
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setForm(emptyForm);
    clearFormImages();
    setFormModalOpen(true);
  };

  const openEditModal = (product: ProductItem) => {
    setEditingProduct(product);
    setForm({
      nameEn: product.nameEn,
      nameAr: product.nameAr,
      descEn: product.descEn || "",
      descAr: product.descAr || "",
      sizeEn: product.sizeEn || "",
      sizeAr: product.sizeAr || "",
      serviceId: product.serviceId,
      sortOrder: String(product.sortOrder),
    });
    newImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setNewImages([]);
    setExistingImages(product.imageUrls || []);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormModalOpen(false);
    setEditingProduct(null);
    clearFormImages();
  };

  const handleFieldChange =
    (field: keyof ProductFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleImagesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setNewImages((current) => [
      ...current,
      ...files.map((file) => ({ file, preview: URL.createObjectURL(file) })),
    ]);
    // Allow picking the same file again later
    event.target.value = "";
  };

  const removeExistingImage = (url: string) => {
    setExistingImages((current) => current.filter((item) => item !== url));
  };

  const removeNewImage = (preview: string) => {
    setNewImages((current) => {
      const target = current.find((img) => img.preview === preview);
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((img) => img.preview !== preview);
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      // Upload newly picked images, keep remaining existing ones
      const uploadedUrls: string[] = [];
      for (const img of newImages) {
        const uploaded = await adminService.uploadImage(img.file);
        uploadedUrls.push(uploaded.url);
      }

      const data: ProductRequestData = {
        nameEn: form.nameEn.trim(),
        nameAr: form.nameAr.trim(),
        serviceId: form.serviceId,
        sortOrder: Number(form.sortOrder) || 0,
        imageUrls: [...existingImages, ...uploadedUrls],
      };
      if (form.descEn.trim()) data.descEn = form.descEn.trim();
      if (form.descAr.trim()) data.descAr = form.descAr.trim();
      if (form.sizeEn.trim()) data.sizeEn = form.sizeEn.trim();
      if (form.sizeAr.trim()) data.sizeAr = form.sizeAr.trim();

      if (editingProduct) {
        await adminProductService.updateProduct(editingProduct.id, data);
        toast.success(t("admin.productUpdated"));
      } else {
        await adminProductService.createProduct(data);
        toast.success(t("admin.productCreated"));
      }
      setFormModalOpen(false);
      setEditingProduct(null);
      clearFormImages();
      loadProducts(page);
    } catch (submitError) {
      toast.error(await getErrorMessage(submitError, t("admin.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (product: ProductItem) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete || deleting) return;
    setDeleting(true);
    try {
      await adminProductService.deleteProduct(productToDelete.id);
      toast.success(t("admin.productDeleted"));
      setDeleteModalOpen(false);
      setProductToDelete(null);
      // If we just removed the last card on a page beyond the first, step back one page
      loadProducts(products.length === 1 && page > 0 ? page - 1 : page);
    } catch (deleteError) {
      toast.error(await getErrorMessage(deleteError, t("admin.deleteFailed")));
    } finally {
      setDeleting(false);
    }
  };

  const serviceTitleOf = (serviceId: string) => {
    const service = serviceOptions.find((s) => s.id === serviceId);
    if (!service) return "—";
    return isArabic ? service.titleAr : service.titleEn;
  };

  // Only truthy when the user has typed something that hasn't been debounced yet.
  const isSearching = searchTerm !== debouncedSearch;

  // Only block the whole page on the true initial mount.
  if (initialLoading) {
    return (
      <div className="admin-loading">
        <span className="auth-spinner auth-spinner-brand"></span>
      </div>
    );
  }

  if (error) {
    return <ConnectionError onRetry={() => loadProducts(page)} />;
  }

  return (
    <div className="admin-products-page">
      {/* Page Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">{t("admin.products")}</h1>
          <p className="admin-page-subtitle">{t("admin.productsDesc")}</p>
        </div>
        <button type="button" className="svc-add-btn" onClick={openCreateModal}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t("admin.addProduct")}
        </button>
      </div>

      {/* ─── Search & Filter Toolbar ─── */}
      <div className="svc-toolbar-wrap">
        <div className="svc-toolbar">
          <div className="svc-search-field">
            <span className="svc-search-field__icon">
              {isSearching ? (
                <span className="svc-search-spinner" />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="17"
                  height="17"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
            </span>
            <input
              type="text"
              className="svc-search-field__input"
              placeholder={t("admin.searchProducts", "Search products...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
            {searchTerm && (
              <button
                className="svc-search-field__clear"
                onClick={handleClearSearch}
                aria-label="Clear search"
                type="button"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <select
            className="prod-filter-select"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            aria-label={t("admin.serviceFilter")}
          >
            <option value="">{t("admin.allServices")}</option>
            {serviceOptions.map((service) => (
              <option key={service.id} value={service.id}>
                {isArabic ? service.titleAr : service.titleEn}
              </option>
            ))}
          </select>
        </div>
        {/* Thin progress bar — visible only during silent re-fetches */}
        <div
          className={`svc-fetch-bar ${isFetching ? "svc-fetch-bar--active" : ""}`}
        />
      </div>

      {/* Product Cards */}
      {isFetching && products.length === 0 ? (
        <div className="admin-loading">
          <span className="auth-spinner auth-spinner-brand"></span>
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          icon={
            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
              <path d="M3.3 8.3L12 13l8.7-4.7" />
              <path d="M12 13v9" />
            </svg>
          }
          title={debouncedSearch || serviceFilter ? t("admin.noSearchResults", "No results found") : t("admin.noProducts")}
          description={debouncedSearch || serviceFilter ? t("admin.noSearchResultsDesc", "Try a different search term.") : t("admin.noProductsDesc")}
        />
      ) : (
        <div className="svc-list">
          {products.map((product) => (
            <article key={product.id} className="svc-card">
              <div className="svc-card__thumb">
                {product.imageUrls?.[0] ? (
                  <img src={resolveApiUrl(product.imageUrls[0])} alt={product.nameEn} />
                ) : (
                  <div className="svc-card__thumb-placeholder">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="svc-card__info">
                <div className="svc-card__title-row">
                  <span className="svc-card__title">{product.nameEn}</span>
                  <span className="svc-card__title-ar">{product.nameAr}</span>
                </div>
                <div className="svc-card__meta">
                  <span className="svc-meta-chip">{serviceTitleOf(product.serviceId)}</span>
                  <span className="svc-meta-chip">#{product.sortOrder}</span>
                  {product.imageUrls?.length > 0 && (
                    <span className="svc-meta-chip">
                      {t("admin.imagesCount", { count: product.imageUrls.length })}
                    </span>
                  )}
                </div>
              </div>

              <div className="svc-card__actions">
                <button
                  type="button"
                  className="svc-action-btn svc-action-btn--edit"
                  onClick={() => openEditModal(product)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  {t("admin.edit")}
                </button>
                <button
                  type="button"
                  className="svc-action-btn svc-action-btn--delete"
                  onClick={() => handleDeleteClick(product)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  {t("admin.delete")}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Numbered pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={loadProducts}
        disabled={isFetching}
      />

      {/* Create / Edit Modal */}
      <Modal
        isOpen={formModalOpen}
        onClose={closeFormModal}
        title={editingProduct ? t("admin.editProduct") : t("admin.addProduct")}
        className="svc-form-modal"
      >
        <form className="svc-form" onSubmit={handleSubmit}>
          <div className="svc-form__row">
            <div className="svc-form__field">
              <label className="svc-form__label">{t("admin.nameEn")}</label>
              <input
                type="text"
                className="svc-form__input"
                value={form.nameEn}
                onChange={handleFieldChange("nameEn")}
                required
                maxLength={160}
              />
            </div>
            <div className="svc-form__field">
              <label className="svc-form__label">{t("admin.nameAr")}</label>
              <input
                type="text"
                className="svc-form__input"
                dir="rtl"
                value={form.nameAr}
                onChange={handleFieldChange("nameAr")}
                required
                maxLength={160}
              />
            </div>
          </div>

          <div className="svc-form__row">
            <div className="svc-form__field">
              <label className="svc-form__label">{t("admin.descEn")}</label>
              <textarea
                className="svc-form__input svc-form__textarea"
                value={form.descEn}
                onChange={handleFieldChange("descEn")}
                maxLength={2000}
                rows={3}
              />
            </div>
            <div className="svc-form__field">
              <label className="svc-form__label">{t("admin.descAr")}</label>
              <textarea
                className="svc-form__input svc-form__textarea"
                dir="rtl"
                value={form.descAr}
                onChange={handleFieldChange("descAr")}
                maxLength={2000}
                rows={3}
              />
            </div>
          </div>

          <div className="svc-form__row">
            <div className="svc-form__field">
              <label className="svc-form__label">{t("admin.sizeEn")}</label>
              <input
                type="text"
                className="svc-form__input"
                value={form.sizeEn}
                onChange={handleFieldChange("sizeEn")}
                maxLength={160}
              />
            </div>
            <div className="svc-form__field">
              <label className="svc-form__label">{t("admin.sizeAr")}</label>
              <input
                type="text"
                className="svc-form__input"
                dir="rtl"
                value={form.sizeAr}
                onChange={handleFieldChange("sizeAr")}
                maxLength={160}
              />
            </div>
          </div>

          <div className="svc-form__row">
            <div className="svc-form__field">
              <label className="svc-form__label">{t("admin.productService")}</label>
              <select
                className="svc-form__input"
                value={form.serviceId}
                onChange={handleFieldChange("serviceId")}
                required
              >
                <option value="" disabled>
                  {t("admin.selectService")}
                </option>
                {serviceOptions.map((service) => (
                  <option key={service.id} value={service.id}>
                    {isArabic ? service.titleAr : service.titleEn}
                  </option>
                ))}
              </select>
            </div>
            <div className="svc-form__field">
              <label className="svc-form__label">{t("admin.sortOrder")}</label>
              <input
                type="number"
                className="svc-form__input"
                min="1"
                value={form.sortOrder}
                onChange={handleFieldChange("sortOrder")}
                required
              />
            </div>
          </div>

          <div className="svc-form__field">
            <label className="svc-form__label">{t("admin.images")}</label>
            <div className="prod-image-upload">
              {(existingImages.length > 0 || newImages.length > 0) && (
                <div className="prod-image-grid">
                  {existingImages.map((url) => (
                    <div key={url} className="prod-image-cell">
                      <img src={resolveApiUrl(url)} alt={t("admin.currentImage")} />
                      <button
                        type="button"
                        className="prod-image-remove"
                        onClick={() => removeExistingImage(url)}
                        aria-label={t("admin.removeImage")}
                      >
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {newImages.map((img) => (
                    <div key={img.preview} className="prod-image-cell prod-image-cell--new">
                      <img src={img.preview} alt={t("admin.newImage")} />
                      <button
                        type="button"
                        className="prod-image-remove"
                        onClick={() => removeNewImage(img.preview)}
                        aria-label={t("admin.removeImage")}
                      >
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="svc-image-upload__picker">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {t("admin.chooseImages")}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  hidden
                />
              </label>
            </div>
          </div>

          <div className="svc-form__actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={closeFormModal}
              disabled={saving}
            >
              {t("admin.cancel")}
            </button>
            <button type="submit" className="btn btn-filled" disabled={saving}>
              {saving ? (
                <span className="svc-btn-loading">
                  <span className="auth-spinner auth-spinner-white" />
                </span>
              ) : (
                t("admin.save")
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title={t("admin.deleteProductConfirmTitle")}
        message={t("admin.deleteProductConfirmDesc")}
        confirmLabel={t("admin.delete") || "Delete"}
        cancelLabel={t("admin.cancel") || "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        type="danger"
      />

      {/* Floating scroll-to-top */}
      <button
        className={`admin-go-to-top ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Go to top"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
}

export default ProductsPage;
